import { useState, useEffect, useRef, useCallback } from 'react';
import { JobRightSheet } from '../../components/jobs/JobRightSheet';
import { RecommendationCard } from '../../components/recommendations/RecommendationCard';
import { apiFetch } from '../../lib/api-client';
import { useResumeContext } from '../../hooks/useResumeContext';
import { RefreshCw, Settings, Target, Filter, Activity } from 'lucide-react';

export type StrategyType = 'HIGH_COMP' | 'RESUME_TAGS' | 'SMALL_CO' | 'LOCATION' | 'EASY_WIN' | 'REMOTE' | 'OTHER';

export type JobRecommendation = {
  jobId: string;
  title: string;
  company: string;
  location: string;
  salary?: string;
  strategy: StrategyType;
  confidence: number; // 0..1
  rationale: string;
  tags: string[]; // e.g., ["TypeScript","K8s","AWS"]
  armId: string; // For feedback
  source: string; // e.g., "JSearch"
  remote: boolean;
  // Legacy fields for compatibility
  id?: string;
  description?: string;
  salaryMin?: number;
  salaryMax?: number;
  employment_type?: string;
};

type FeedResponse = {
  success: boolean;
  resumeId?: string;
  items?: JobRecommendation[];
  nextCursor?: string | null;
  cache?: {
    hit: boolean;
    key: string;
  };
  error?: {
    code: string;
    message: string;
  };
};

export default function DiscoverPage() {
  const { selectedResumeId, selectedResume, loading: resumeLoading } = useResumeContext();
  const [items, setItems] = useState<JobRecommendation[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [strategyInfo] = useState<{ objective?: string; exploration?: number }>({});
  
  // Track optimistic UI states
  const [optimisticActions, setOptimisticActions] = useState<Map<string, 'saving' | 'saved' | 'applying' | 'applied' | 'rejecting' | 'rejected'>>(new Map());

  const selected = selectedIndex >= 0 ? items[selectedIndex] : null;

  const loadFeed = useCallback(async (next?: string | null, isRefresh?: boolean) => {
    if (loading || resumeLoading || !selectedResumeId) return;
    setLoading(true);
    setError(null);
    
    try {
      const url = new URL('/recommendations', window.location.origin);
      url.searchParams.set('resumeId', selectedResumeId);
      if (next) url.searchParams.set('cursor', next);
      url.searchParams.set('page_size', '10');
      
      const res = await apiFetch(url.pathname + url.search);
      
      if (!res.ok) {
        throw new Error(`Feed error ${res.status}`);
      }
      
      const data = (await res.json()) as FeedResponse;
      
      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to load recommendations');
      }
      
      const recommendations = data.items || [];
      
      // Transform items to ensure compatibility
      const transformedItems = recommendations.map(item => ({
        ...item,
        id: item.jobId, // Legacy compatibility
        confidence: item.confidence || 0.7,
        tags: item.tags || [],
        rationale: item.rationale || 'Job matches your profile'
      }));
      
      if (isRefresh) {
        setItems(transformedItems);
      } else {
        setItems((prev) => [...prev, ...transformedItems]);
      }
      
      setCursor(data.nextCursor ?? null);
      
    } catch (e: any) {
      console.error('Failed to load recommendations:', e);
      setError(e?.message || 'Failed to load recommendations');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loading, resumeLoading, selectedResumeId]);

  // Initial load and reload when resume changes
  useEffect(() => {
    if (selectedResumeId && !resumeLoading) {
      loadFeed(null, true); // Force refresh when resume changes
    }
  }, [selectedResumeId, resumeLoading]);

  // Infinite scroll via IntersectionObserver
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!loadMoreRef.current) return;
    const el = loadMoreRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && cursor && !loading) {
            loadFeed(cursor);
          }
        }
      },
      { rootMargin: '200px' }
    );
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [cursor, loadFeed, loading]);

  // Keyboard shortcuts: j/k next/prev, o open, s save, r reject
  useEffect(() => {
    const handler = (ev: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (ev.target && (ev.target as HTMLElement).tagName.match(/INPUT|TEXTAREA|SELECT/)) return;
      if (items.length === 0) return;
      
      if (ev.key === 'j') {
        ev.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, items.length - 1));
      } else if (ev.key === 'k') {
        ev.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (ev.key === 'o') {
        ev.preventDefault();
        if (selectedIndex < 0) setSelectedIndex(0);
        setSheetOpen(true);
      } else if (ev.key.toLowerCase() === 's') {
        ev.preventDefault();
        if (selected) onSave(selected);
      } else if (ev.key.toLowerCase() === 'r') {
        ev.preventDefault();
        if (selected) onReject(selected);
      }
    };
    
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items.length, selectedIndex, selected]);

  async function onSave(job: JobRecommendation) {
    const jobKey = job.jobId || job.id || '';
    
    try {
      // Optimistic UI update
      setOptimisticActions(prev => new Map(prev).set(jobKey, 'saving'));
      
      await apiFetch(`/api/v2/jobs/${encodeURIComponent(jobKey)}/save`, { 
        method: 'POST',
        body: JSON.stringify({ 
          resumeId: selectedResumeId,
          armId: job.armId 
        })
      });
      
      // Update to saved state
      setOptimisticActions(prev => new Map(prev).set(jobKey, 'saved'));
      
      // Clear after 2 seconds
      setTimeout(() => {
        setOptimisticActions(prev => {
          const next = new Map(prev);
          next.delete(jobKey);
          return next;
        });
      }, 2000);
      
    } catch (error) {
      console.error('Failed to save job:', error);
      // Revert optimistic update on error
      setOptimisticActions(prev => {
        const next = new Map(prev);
        next.delete(jobKey);
        return next;
      });
    }
  }

  async function onApply(job: JobRecommendation) {
    const jobKey = job.jobId || job.id || '';
    
    try {
      // Optimistic UI update
      setOptimisticActions(prev => new Map(prev).set(jobKey, 'applying'));
      
      await apiFetch(`/api/v2/jobs/${encodeURIComponent(jobKey)}/apply-intent`, { 
        method: 'POST',
        body: JSON.stringify({ 
          resumeId: selectedResumeId,
          armId: job.armId 
        })
      });
      
      // Update to applied state
      setOptimisticActions(prev => new Map(prev).set(jobKey, 'applied'));
      
      // Clear after 2 seconds
      setTimeout(() => {
        setOptimisticActions(prev => {
          const next = new Map(prev);
          next.delete(jobKey);
          return next;
        });
      }, 2000);
      
    } catch (error) {
      console.error('Failed to apply:', error);
      // Revert optimistic update on error
      setOptimisticActions(prev => {
        const next = new Map(prev);
        next.delete(jobKey);
        return next;
      });
    }
  }

  async function onReject(job: JobRecommendation) {
    const jobKey = job.jobId || job.id || '';
    
    try {
      // Optimistic UI update
      setOptimisticActions(prev => new Map(prev).set(jobKey, 'rejecting'));
      
      await apiFetch(`/api/v2/jobs/${encodeURIComponent(jobKey)}/reject`, { 
        method: 'POST',
        body: JSON.stringify({ 
          resumeId: selectedResumeId,
          armId: job.armId 
        })
      });
      
      // Update to rejected state and remove from list
      setOptimisticActions(prev => new Map(prev).set(jobKey, 'rejected'));
      
      // Remove from items list after short delay
      setTimeout(() => {
        setItems(prevItems => prevItems.filter(item => (item.jobId || item.id) !== jobKey));
        setOptimisticActions(prev => {
          const next = new Map(prev);
          next.delete(jobKey);
          return next;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Failed to reject job:', error);
      // Revert optimistic update on error
      setOptimisticActions(prev => {
        const next = new Map(prev);
        next.delete(jobKey);
        return next;
      });
    }
  }

  async function onFeedback(job: JobRecommendation, signal: string) {
    try {
      await apiFetch('/strategy/feedback', {
        method: 'POST',
        body: JSON.stringify({ 
          jobId: job.jobId || job.id || '', 
          armId: job.armId,
          resumeId: selectedResumeId,
          signal 
        })
      });
    } catch (error) {
      console.error('Failed to send feedback:', error);
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    setCursor(null);
    await loadFeed(null, true);
  };

  const handleCardClick = (index: number) => {
    setSelectedIndex(index);
    setSheetOpen(true);
  };

  // Show loading state while resume context loads
  if (resumeLoading) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-6 space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-primary/20 animate-pulse rounded"></div>
            <span className="text-muted-foreground">Loading resumes...</span>
          </div>
        </div>
      </div>
    );
  }

  // Show message when no resume is selected
  if (!selectedResumeId) {
    return (
      <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-6 space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <h2 className="text-lg font-medium text-foreground mb-2">No Resume Selected</h2>
            <p className="text-muted-foreground mb-4">Please upload a resume to get personalized job recommendations.</p>
            <a href="/resumes" className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity">
              Upload Resume
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-4 md:px-6 py-6 space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Discover</h1>
          <p className="text-sm text-muted-foreground">
            AI-picked roles for <span className="font-medium">{selectedResume?.filename}</span>. Review, teach, repeat.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-xl bg-primary px-4 py-2 text-primary-foreground shadow-sm hover:opacity-95 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button 
            onClick={() => window.location.href = '/strategy'}
            className="rounded-xl border border-border px-4 py-2 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all flex items-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Tune Strategy
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        {/* Feed */}
        <div className="lg:col-span-8 space-y-4">
          {items.map((job, idx) => {
            const jobKey = job.jobId || job.id || '';
            const optimisticAction = optimisticActions.get(jobKey);
            
            return (
              <div key={jobKey || idx} className={idx === selectedIndex ? 'ring-2 ring-primary ring-offset-2 rounded-2xl' : ''}>
                <RecommendationCard
                  job={job}
                  selected={idx === selectedIndex}
                  onClick={() => handleCardClick(idx)}
                  onSave={onSave}
                  onApply={onApply}
                  onReject={onReject}
                  onFeedback={onFeedback}
                  optimisticAction={optimisticAction}
                />
              </div>
            );
          })}
          
          {/* Loading indicator / Load more trigger */}
          <div ref={loadMoreRef} className="py-4 text-center text-sm text-muted-foreground">
            {loading && items.length === 0 ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-2xl border border-border bg-card p-4 animate-pulse">
                    <div className="h-5 bg-muted/50 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-muted/50 rounded w-1/2 mb-3"></div>
                    <div className="h-3 bg-muted/50 rounded w-full"></div>
                  </div>
                ))}
              </div>
            ) : loading ? (
              'Loading more…'
            ) : cursor ? (
              'Scroll to load more'
            ) : items.length > 0 ? (
              "You're all caught up"
            ) : null}
            {error && <div className="mt-2 text-red-500">{error}</div>}
          </div>
          
          {/* Empty state */}
          {!loading && items.length === 0 && (
            <div className="rounded-2xl border border-border bg-card p-8 text-center">
              <div className="bg-champagne-pink/10 rounded-2xl py-12">
                <Target className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No recommendations yet</h3>
                <p className="text-muted-foreground mb-4">
                  Upload a resume or refresh to get AI-powered job recommendations.
                </p>
                <button 
                  onClick={handleRefresh}
                  className="rounded-xl bg-primary px-4 py-2 text-primary-foreground shadow-sm hover:opacity-95"
                >
                  Get Recommendations
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right column: snapshot & filters & activity */}
        <div className="lg:col-span-4 space-y-4">
          {/* Strategy Snapshot */}
          <div className="rounded-2xl border border-border bg-card p-4 shadow-card hover:border-border-hover hover:shadow-card-hover transition-all">
            <div className="mb-2 text-sm text-muted-foreground">Strategy</div>
            <div className="text-lg font-medium">
              {strategyInfo.objective || 'High Comp'} • Exploration {strategyInfo.exploration || 15}%
            </div>
            <div className="mt-2 text-sm">Top signals: Resume tags, Feedback</div>
            <div className="mt-3">
              <button 
                onClick={() => window.location.href = '/strategy/lab'}
                className="rounded-xl border border-border px-3 py-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
              >
                Open Strategy Lab
              </button>
            </div>
          </div>
          
          {/* Feed Filters */}
          <div className="rounded-2xl border border-border bg-card p-4 shadow-card hover:border-border-hover hover:shadow-card-hover transition-all">
            <div className="mb-2 text-sm text-muted-foreground flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Feed Filters
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              {['Remote', 'Senior+', '>$180k', 'US only'].map((filter) => (
                <button 
                  key={filter} 
                  className="rounded-xl border border-border px-3 py-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
          
          {/* Activity */}
          <div className="rounded-2xl border border-border bg-card p-4 shadow-card hover:border-border-hover hover:shadow-card-hover transition-all">
            <div className="mb-2 text-sm text-muted-foreground flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Activity
            </div>
            <ul className="space-y-1 text-sm">
              <li className="flex items-center gap-2">
                <span className="text-positive">✓</span>
                Parsed "Resume_2025.pdf"
              </li>
              <li className="flex items-center gap-2">
                <span className="text-info">★</span>
                Saved "Dataco — Senior SWE"
              </li>
              <li className="flex items-center gap-2">
                <span className="text-muted-foreground">⟳</span>
                Recomputed recommendations
              </li>
            </ul>
          </div>
          
          {/* Keyboard shortcuts help */}
          <div className="rounded-2xl border border-border bg-card p-4 shadow-card text-xs text-muted-foreground">
            <div className="mb-2 font-medium">Keyboard Shortcuts</div>
            <div className="space-y-1">
              <div><kbd className="bg-muted px-1 rounded">J/K</kbd> Navigate up/down</div>
              <div><kbd className="bg-muted px-1 rounded">O</kbd> Open details</div>
              <div><kbd className="bg-muted px-1 rounded">S</kbd> Save job</div>
              <div><kbd className="bg-muted px-1 rounded">R</kbd> Reject job</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right sheet */}
      <JobRightSheet 
        open={sheetOpen} 
        onClose={() => setSheetOpen(false)} 
        job={selected || undefined} 
        onSave={onSave} 
        onApply={onApply} 
        onReject={onReject} 
        onFeedback={onFeedback}
        optimisticAction={selected ? optimisticActions.get(selected.jobId || selected.id || '') : undefined}
      />
    </div>
  );
}

